import React, { FC } from 'react';
import { reduxForm, Field, getFormValues } from 'redux-form';
import { checkBox, muiCustomSelect, newThemeInput } from '_fields/inputs';
import { lowerCase, words } from '_fields/normalizers';
import ContinueButton from '@components/_shared/Continue/Button';
import { FormPageTopPart, FormPageTopPartBcmShort } from '@components/_shared/FormPageTopPart/FormPageTopPart';
import { connect, useStore } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { formValueSelector } from 'redux-form';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Spinner } from '@components/Spinner/Spinner';
import { Helmet } from 'react-helmet';
import { Button, cn, LoadingTento, Stack, Text, useHasFullLoaderTimeElapsed } from 'shared-components';
import { NaicsField } from '@components/Apply/MoreDetailsOnYourBusiness/NaicsField';
import { AddressField } from '@components/Apply/MoreDetailsOnYourBusiness/AddressField';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { parseCurrency } from '_fields/parsers';
import { ApplyWizardSteps, STEP_EQ_LEAD_PRODUCT_DETAILS } from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import { ContinueButtonWide } from '@components/_shared/Continue/ContinueButton2';
import withLeadOnSubmit from '@components/Apply/withOnSubmit/withLeadOnSubmit';
import ensureNotLoggedIn from '../../../hooks/ensureNotLoggedIn';
import { ensureLeadIds } from '../../../hooks/ensureLeadIds';
import { rangeInput } from '_fields/inputs';
import { required } from '@components/_fields/validation';
import { EQFlowFooter } from '../_shared/EQFlowFooter';

type EQLeadEquipmentInfoProps = {
  handleSubmit: () => void;
  invalid: boolean;
  saving?: boolean;
  prefilled_loan_amount?: number;
  prefilled_equipment_description?: string;
  loan_amount?: number;
  equipment_description?: string;
};

const EQLeadEquipmentInfoWrapper: FC<EQLeadEquipmentInfoProps> = (props) => {
  const auth0 = useAuth0();

  if (auth0.isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" fullWidth>
        <Spinner />
      </Stack>
    );
  }

  return <EQLeadEquipmentInfoMainComponent {...props} />;
};

const EQLeadEquipmentInfoMainComponent: FC<EQLeadEquipmentInfoProps> = ({
  handleSubmit,
  invalid,
  saving: savingOnSubmit,
  prefilled_loan_amount,
  prefilled_equipment_description,
  loan_amount,
  equipment_description,
}) => {
  const { saving } = useApplyWizardContext();
  const form = useForm();

  const store = useStore();
  const latestValues = getFormValues('application')(store.getState() as RootState) || {};
  const validationOutput = reduxFormZodValidate(ApplyWizardSteps[STEP_EQ_LEAD_PRODUCT_DETAILS].zodSchema)(latestValues);

  return (
    <div>
      <Helmet>
        <title>More Details on Equipment</title>
      </Helmet>
      <div className="w-full flex flex-col gap-[20px] mt-[16px]">
        <Text variant="subheadline-sm2" className="!text-[16px] !font-extended py-[20px] !leading-[100%]">
          TELL US HOW WE CAN HELP
        </Text>
        <Text variant="subheadline-sm2" className="!text-[20px] !font-standard !leading-[30px]">
          Financing Details
        </Text>
        <Form {...form}>
          <form onSubmit={handleSubmit}>
            <Stack gapPx="40px">
              <Stack gapPx="30px" justifyItems="center" templateColumns="1fr" fullWidth>
                <Field
                  name="loan_amount"
                  label="Amount (USD)"
                  variant="transparent-black"
                  component={newThemeInput}
                  className="amount"
                  placeholder="Financing amount"
                  formatAsNumber
                  parse={parseCurrency}
                  data-testid="loan-amount"
                />

                <Stack gapVariant="input-to-input" justifyItems="center" alignItems="center" fullWidth>
                  <div className="h-[58px] mt-[10px] max-w-[368px] w-full">
                    <Field
                      name="loan_amount"
                      component={rangeInput}
                      min={5000}
                      max={500000}
                      data-testid="loan_amount_slider"
                    />
                  </div>
                </Stack>

                <Field
                  name="equipment_description"
                  variant="transparent-black"
                  component={newThemeInput}
                  type="text"
                  validate={[required]}
                  placeholder="Product description"
                  labelText="Product description"
                  labelClassName="infoFormCustomLabel"
                  data-testid="equipment_description"
                  className="equipment-description"
                />
              </Stack>
              <div className="w-full flex flex-col items-center justify-center gap-[10px] mx-auto">
                <Button
                  variant="primary-green"
                  color="primary"
                  type="submit"
                  disabled={invalid || saving}
                  isLoading={saving || savingOnSubmit}
                  data-testid="info_continue"
                  className="!max-w-[592px] !h-[50px] border-[1px] border-spark2 !py-[12.5px] font-condensed leading-[120%] px-[50px] !text-[16px]"
                >
                  CONTINUE
                </Button>
                <Text variant="body-form-label" className="!text-[16px] !leading-[18px] !font-condensed text-charcoal2">
                  *NO HARD CREDIT CHECKS
                </Text>
              </div>
            </Stack>
          </form>

          <EQFlowFooter />
        </Form>
      </div>
    </div>
  );
};

const EQLeadEquipmentInfoWrapper2 = reduxForm<EQLeadEquipmentInfoProps, EQLeadEquipmentInfoProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_EQ_LEAD_PRODUCT_DETAILS].zodSchema),
})(EQLeadEquipmentInfoWrapper);
const appSelector = formValueSelector('application');
const prefilledSelector = formValueSelector('prefilled');

const mapStateToProps = (state: RootState) => {
  return {
    loan_amount: appSelector(state, 'loan_amount'),
    equipment_description: appSelector(state, 'equipment_description'),
    prefilled_loan_amount: prefilledSelector(state, 'loan_amount'),
    prefilled_equipment_description: prefilledSelector(state, 'equipment_description'),
  };
};

export const EQLeadEquipmentInfoStep = ensureLeadIds(
  ensureNotLoggedIn(
    withLeadOnSubmit(connect(mapStateToProps)(EQLeadEquipmentInfoWrapper2), STEP_EQ_LEAD_PRODUCT_DETAILS)
  )
);
